import {LfI18n, LfStorage} from '@lightweightform/core';
import {
  I18N_SELECTABLE_NO_OPTIONS_TEXT_KEY,
  I18N_SELECTABLE_OPTIONS_KEY,
  I18N_TABLE_COLUMN_LABELS_KEY,
  I18N_VALUE_CODE_KEY,
  I18N_VALUE_HELP_MESSAGE_KEY,
  I18N_VALUE_LABEL_KEY
} from 'lf-at-theme';
import {
  allCountriesCodeOptions,
  euCountriesExceptPtCodeOptions,
  memberStatesCodeOptions,
  memberStatesExceptPtCodeOptions,
  nonEuCountriesCodeOptions,
  onlyPtCountryCodeOptions,
  peculiarCountriesCodeOptions,
  allCountriesCodeOptionsWithoutArtificialCodes
} from '../../catalog/countries.i18n.pt-PT';
import {ERRO_REGISTO} from '../../catalog/server/serverCatalogs';
import {getKtData} from './helpers';

//Na versão 7 deixará de ser possivel de utilizar codigos ficticios na morada
//Por isso apenas aparecerão esses codigos na lista quando se está a consultar um registo (ou seja no modo de detalhe)
function peculiarAddressCountriesByFieldFn(parentCountryPath) {
  return (ctx: LfStorage) => {
    const initialValues = ctx.getStateProperty('/', 'initialValues') || {};
    const filterArtificialCountryCodes = !initialValues.mode.detail;

    return peculiarCountriesCodeOptions(ctx.get(parentCountryPath),filterArtificialCountryCodes)
  }
}

function peculiarCountriesByFieldFn(parentCountryPath) {
  return ctx => peculiarCountriesCodeOptions(ctx.get(parentCountryPath),false)
}

const countriesOfBusinessOptions = function ({defaultNonResident}: { defaultNonResident: boolean }) {
  return (ctx: LfStorage) => {
    const initialValues = ctx.getStateProperty('/', 'initialValues') || {};
    const isNonResident = initialValues.isNonResident ?? defaultNonResident;
    const isByIntermediary = initialValues.registeredBy == 'INTERMEDIARY';
    const isDetail = !!initialValues.mode.detail;
    return isDetail
      ? allCountriesCodeOptions
      : isByIntermediary
        ? allCountriesCodeOptions
        : isNonResident
          ? [...nonEuCountriesCodeOptions, ...euCountriesExceptPtCodeOptions]
          : onlyPtCountryCodeOptions;
  };
};


const contactCountriesOptions = allCountriesCodeOptionsWithoutArtificialCodes;
const fixedEstabIssuingCountries = memberStatesExceptPtCodeOptions;
const fixedEstabAddressCountries = allCountriesCodeOptionsWithoutArtificialCodes;
const idsInOtherMSsCountries = memberStatesExceptPtCodeOptions;
const prevOrCurrOssVatIdCountries = memberStatesCodeOptions;

const prevOrCurrOssVatIdSchemes = [
  {value: 'UNION', label: 'União'},
  {value: 'NON_UNION', label: 'extra-União'},
  {value: 'IMPORT', label: 'Importação'},
];

export const yesNoOptions = [
  {value: true, code: '', label: 'Sim'},
  {value: false, code: '', label: 'Não'},
];

export const addressTypeOptions = [
  {value: 'structured', code: '', label: 'Estruturada'},
  {value: 'freeText', code: '', label: 'Texto Livre'},
];

export const contactNameTypeOptions = [
  {value: 'structured', code: '', label: 'Estruturado'},
  {value: 'freeText', code: '', label: 'Texto Livre'},
];

export const taxVatIdColumnNames = {
  scheme: 'Regime',
  issuingCountry: 'País',
  vatIdNum: 'Número IVA',
};

export const sharedFormI18nPtPT: (allowsResidents: boolean) => Record<string, any> = (allowsResidents) => (LfI18n.mergeTranslations(
  {},
  ERRO_REGISTO.pt_PT,
  {
    '*': {
      validations: {
        ONLY_SINGLE_SPACES_ALLOWED: 'Não podem existir espaços consecutivos neste campo.',

        VAT_ID_ONE_TYPE_REQUIRED: 'Tem de preencher o numero de IVA.',
        VAT_ID_ONLY_ONE_ALLOWED: 'Só pode preencher ou um [Número IVA](lf://./vatIdNum) ou um [Número Fiscal](lf://./taxRefNum).',
        VAT_ID_PARTIALLY_FILLED: 'Tem de preencher ou um [Número IVA](lf://./vatIdNum) ou um [Número Fiscal](lf://./taxRefNum). Deve preencher também o [País Emissor](lf://./issuingCountry).',

        VAT_ID_VAT_TYPE_NOT_ALLOWED: 'Este tipo de número de identificação não é permitido.',
        VAT_ID_TAX_TYPE_NOT_ALLOWED: 'Este tipo de número de identificação não é permitido.',
        VAT_ID_SCHEME_NOT_ALLOWED: 'Este regime não é permitido.',

        VAT_ID_INVALID_VAT_FORMAT: 'Números de identificação de IVA só podem ter de 1 a 12 dos seguintes carateres: letras maiúsculas, \'+\', \'*\', ou dígitos.',
        VAT_ID_INVALID_VOES_FORMAT: (data) => `Números VOES têm de estar no formato EUnnnaaaaaa, onde nnn é o código numérico do país emissor (${getKtData(data, 'n3Code')}) e aaaaaa é o identificador.`,
        VAT_ID_INVALID_IOSS_FORMAT: (data) => `Números IOSS têm de estar no formato IMnnnaaaaaaa, onde nnn é o código numérico do país emissor (${getKtData(data, 'n3Code')}) e aaaaaa é o identificador.`,
        VAT_ID_INVALID_INT_FORMAT: (data) => `Números INT têm de estar no formato INnnnaaaaaaa, onde nnn é o código numérico do país emissor (${getKtData(data, 'n3Code')}) e aaaaaa é o identificador.`,
        VAT_ID_TAX_REF_NUM_INVALID_FORMAT: 'Números de identificação têm de ter de 1 a 50 carateres, não começar ou acabar em espaços, e não ter espaços com múltiplos carateres.',

        ADDRESS_COUNTRY_INVALID: '[País de morada](lf://./address/country) inválido',
        ADDRESS_STRUCTURED_FIELDS_BUT_NOT_STRUCTURED: 'Campo de morada estruturada preenchido mas o [tipo de morada](lf://./address/type) não é estruturado.',

        CONTACT_NAME_STRUCTURED_FIELDS_BUT_NOT_STRUCTURED: 'Campo de contacto estruturado preenchido mas o [tipo de contacto](lf://./contactName/type) não é estruturado.',

        BANK_ACCOUNT_INFO_NOT_ALLOWED: 'Informação bancária não permitida.',

        ACCOUNT_HOLDER_INVALID: 'O nome colocado como titular de conta contém carateres inválidos. São apenas permitidos carateres alfanuméricos (sem acentos e cedilhas), \'?\', \'/\', \'\ \' \, \'-\', \':\', \'(\', \')\', \'.\', \',\', \'\'\', \'+\', \'.\'. Adicionalmente, apenas são permitidos caracteres alfanuméricos no início do nome.',

        COMMENCEMENT_DATE_NOT_ALLOWED: 'Data de começo de fornecimento não é permitida.',
        COMMENCEMENT_DATE_BEFORE_OSS_START: 'Data de começo de fornecimento não deve ser antes do começo do ECOM-OSS.',

        HAVE_TRADING_NAMES_BUT_NO_COMPANY_NAME: 'Deve preencher [Nome da empresa](lf://./companyName) antes de preencher outras designações comerciais.',

        FIXED_ESTABLISHMENT_NOT_REAL: 'Apenas são permitidos estabelecimentos estáveis em Estados-Membros onde tem estabelecimento. Tem de preencher um Número de identificação IVA ou um Número de identificação fiscal.',
        FIXED_ESTABLISHMENT_NOT_STOCK: 'Quando não tem estabelecimento na União, não pode preencher o número de identificação do estabelecimento.',
      }
    },

    '/?': {
      identification: 'Identificação',
      contact: 'Contacto',
      loadedPrevRegistration: 'Pré-carregámos a informação do seu registo anterior. Por favor reveja-a antes de submeter.',
    },

    '/?/currVatId/issuingCountry': {
      [I18N_VALUE_CODE_KEY]: '1',
    },

    '/?/companyName': {
      label: 'Nome da empresa',
      [I18N_VALUE_CODE_KEY]: '3'
    },

    '/?/tradingNames': {
      label: 'Designação(ões) comercial(is) da empresa',
      [I18N_VALUE_HELP_MESSAGE_KEY]: 'Caso seja diferente do nome da empresa',
      [I18N_TABLE_COLUMN_LABELS_KEY]: {name: 'Designação comercial da empresa'},
      [I18N_VALUE_CODE_KEY]: '4'
    },

    '/?/tradingNames/?/name': {
      [I18N_VALUE_LABEL_KEY]: 'Designação comercial da empresa',
      [I18N_VALUE_CODE_KEY]: '4'
    },

    '/?/countryOfBusiness': {
      label: 'País de Sede',
      [I18N_SELECTABLE_OPTIONS_KEY]: countriesOfBusinessOptions({defaultNonResident: !allowsResidents}), // If we allow residents, if nothing is said, we assume we're dealing with a resident.
      [I18N_VALUE_CODE_KEY]: '6',
      validations: {
        COUNTRY_OF_BUSINESS_INVALID: '[País de sede](lf://.) inválido',
        COUNTRY_OF_BUSINESS_EU_NO_REGION: 'Um Estado-Membro só pode ser [país de sede](lf://.) se preencher uma [Região/Território](lf://../cobRegion) fora da União associada a esse Estado-Membro.',
        COUNTRY_OF_BUSINESS_GB_NO_REGION: 'Ao escolher Reino Unido como [país de sede](lf://.) deve especificar uma [Região/Território](lf://../cobRegion).',
      },
    },
    '/?/cobRegion': {
      label: 'Região/Território',
      [I18N_VALUE_HELP_MESSAGE_KEY]: 'Territórios ou regiões  sujeitos a fiscalidade privilegiada',
      [I18N_SELECTABLE_OPTIONS_KEY]: peculiarCountriesByFieldFn('../countryOfBusiness'),
      [I18N_SELECTABLE_NO_OPTIONS_TEXT_KEY]: 'Sem opções',
      validations: {
        REGION_WRONG_COUNTRY_OF_BUSINESS: 'Esta região não pertence ao [país](lf://../countryOfBusiness) preenchido.',
        REGION_NOT_OUTSIDE_UNION: 'Esta região não está fora da União',
      },
    },

    /* Address */
    '/?/address': {
      label: 'Morada',
      validations: {
        ADDRESS_UK_NO_REGION: 'Ao escolher Reino Unido como [país](lf://./country), deve especificar uma [região](lf://./region).',
      }
    },
    '/?/address/type': {
      label: 'Tipo de Morada',
      [I18N_SELECTABLE_OPTIONS_KEY]: addressTypeOptions,
      [I18N_VALUE_CODE_KEY]: '5'
    },
    /* Structured */
    '/?/address/street': {label: 'Rua'},
    '/?/address/buildingNumber': {label: 'Número'},
    '/?/address/floorNumber': {label: 'Andar'},
    '/?/address/postCode': {
      label: 'Código Postal',
      validations: {
        ADDRESS_POST_CODE_INVALID_FORMAT: 'O código postal tem de ser preenchido com formato NNNN-NNN.'
      }
    },
    '/?/address/city': {label: 'Localidade'},
    '/?/address/country': {label: 'País', [I18N_SELECTABLE_OPTIONS_KEY]: contactCountriesOptions},
    '/?/address/region': {
      label: 'Região/Território',
      [I18N_VALUE_HELP_MESSAGE_KEY]: 'Territórios ou regiões  sujeitos a fiscalidade privilegiada',
      [I18N_SELECTABLE_OPTIONS_KEY]: peculiarAddressCountriesByFieldFn('../country'),
      [I18N_SELECTABLE_NO_OPTIONS_TEXT_KEY]: 'Sem opções',
      validations: {
        ADDRESS_REGION_WRONG_COUNTRY: 'Esta região não pertence ao [país](lf://../country) preenchido.',
        ADDRESS_WITH_ARTIFICIAL_CODE_NOT_ALLOWED: 'A [região](lf://../region) é inválida.',
      },
    },
    /* Structured - Extra address fields */
    '/?/address/suite': {label: 'Fração'},
    '/?/address/district': {label: 'Distrito'},
    '/?/address/postOfficeBox': {label: 'Apartado'},
    '/?/address/countrySubEntity': {label: 'Região Autónoma'},
    '/?/address/otherLocalId': {label: 'Outro'},
    /* Free Text */
    '/?/address/freeText': {label: 'Morada Livre'},

    '/?/websites': {
      label: 'Sítio(s) Web do sujeito passivo, se existir(em)',
      [I18N_TABLE_COLUMN_LABELS_KEY]: {name: 'Sítio Web'},
      [I18N_VALUE_CODE_KEY]: '8'
    },
    '/?/websites/?': {
      validations: {
        WEBSITE_INVALID: 'URL de Sítio Web com formato inválido.',
        WEBSITE_NOT_GIVEN: 'É necessário fornecer pelo menos um Sítio Web.',
      }
    },
    '/?/websites/?/name': {label: 'Nome'},

    /* Contact Name */
    '/?/contactName': {
      label: 'Pessoa para contacto',
    },
    '/?/contactName/type': {
      label: 'Tipo de nome',
      [I18N_SELECTABLE_OPTIONS_KEY]: contactNameTypeOptions,
      [I18N_VALUE_CODE_KEY]: '9'
    },
    '/?/contactName/precedingTitle': {label: 'Título Precedente'},
    '/?/contactName/firstName': {label: 'Primeiro Nome'},
    '/?/contactName/lastNamePrefix': {label: 'Prefixo do nome'},
    '/?/contactName/lastName': {label: 'Último Nome'},
    '/?/contactName/generalSuffix': {label: 'Sufixo Geral'},
    '/?/contactName/maidenName': {label: 'Nome de solteiro/a'},
    /* Contact Name - Repeatable parts */
    '/?/contactName/titles': {label: 'Títulos'},
    '/?/contactName/middleNames': {label: 'Nomes do meio'},
    '/?/contactName/generationIdentifiers': {label: 'Identificadores de geração'},
    '/?/contactName/suffixes': {label: 'Sufixos'},
    '/?/contactName/titles/?/name': {label: 'Nome'},
    '/?/contactName/middleNames/?/name': {label: 'Nome'},
    '/?/contactName/generationIdentifiers/?/name': {label: 'Nome'},
    '/?/contactName/suffixes/?/name': {label: 'Nome'},
    /* Free text name */
    '/?/contactName/freeText': {label: 'Nome em formato livre'},
    /* More Contact Info */
    '/?/contactInfo': {
      label: 'Informações de contacto',
    },
    '/?/contactInfo/email': {
      label: 'Endereço de correio eletrónico do sujeito passivo',
      [I18N_VALUE_CODE_KEY]: '7',
      validations: {EMAIL_INVALID: 'Formato de email inválido.'}
    },
    '/?/contactInfo/emailConfirmation': {
      label: 'Confirmação do endereço de correio eletrónico',
      validations: {EMAIL_CONFIRMATION_NOT_EQUAL: 'Os endereços de de correio eletrónico não iguais.'}
    },
    '/?/contactInfo/telephone': {
      label: 'Número de telefone',
      [I18N_VALUE_CODE_KEY]: '10',
      validations: {
        TELEPHONE_INVALID: 'Número de telefone inválido.'
      }
    },

    // Bank account information
    '/?/bankAccountInfo': {
      label: 'Informação para restituições',
      validations: {
        BANK_ACCOUNT_ID_NOT_PROVIDED: 'É necessário preencher o campo IBAN.',
      }
    },
    '/?/bankAccountInfo/iban': {
      label: 'Número IBAN',
      [I18N_VALUE_CODE_KEY]: '11',
      validations: {
        IBAN_INVALID: 'O Número IBAN inválido.',
      }
    },
    '/?/bankAccountInfo/bic': {
      label: 'Número BIC',
      [I18N_VALUE_CODE_KEY]: '12',
      validations: {
        BIC_INVALID: 'Número BIC Inválido.'
      },
    },
    '/?/bankAccountInfo/accountHolder': {label: 'Titular de conta'},

    // Fixed establishments
    '/?/fixedEstablishments': {
      label: 'Estabelecimentos estáveis/Locais de expedição',
      validations: {
        FIXED_ESTABLISHMENT_DUPLICATED: 'Não pode existir mais de um Estabelecimento Estável com o mesmo VAT.'
      },
    },
    '/?/fixedEstablishments/?': {
      fixedEstablishmentsVatId: 'Identificação',
      address: 'Morada',

      label: (ctx: LfStorage) => {
        const val = ctx.get('./taxIdentif') as { issuingCountry: string, vatIdNum: string, taxRefNum: string };
        return val.issuingCountry + '/' + (val.vatIdNum || val.taxRefNum);
      },
    },
    '/?/fixedEstablishments/?/taxIdentif': {
      label: '13.1'
    },
    '/?/fixedEstablishments/?/taxIdentif/issuingCountry': {
      label: 'País',
      [I18N_VALUE_CODE_KEY]: '13.1',
      [I18N_SELECTABLE_OPTIONS_KEY]: fixedEstabIssuingCountries
    },
    '/?/fixedEstablishments/?/taxIdentif/vatIdNum': {
      label: 'Numero de identificação IVA',
    },
    '/?/fixedEstablishments/?/taxIdentif/taxRefNum': {
      label: 'Numero de Identificação Fiscal'
    },
    '/?/fixedEstablishments/?/tradingName': {
      label: 'Nome comercial',
      [I18N_VALUE_CODE_KEY]: '14.1',
    },
    '/?/fixedEstablishments/?/address': {
      label: 'Morada',
      validations: {
        ADDRESS_UK_NO_REGION: 'Ao escolher Reino Unido como [país](lf://./country), deve especificar uma [região](lf://./region).',
      }
    },
    '/?/fixedEstablishments/?/address/type': {
      label: 'Tipo de Morada',
      [I18N_VALUE_CODE_KEY]: '14.1',
      [I18N_VALUE_HELP_MESSAGE_KEY]: 'Endereço postal completo e designação comercial dos estabelecimentos estáveis e dos locais a partir de onde os bens são expedidos ou transportados em Estados-Membros que não seja(m) o Estado-Membro de identificação',
      [I18N_SELECTABLE_OPTIONS_KEY]: addressTypeOptions
    },
    '/?/fixedEstablishments/?/address/street': {label: 'Rua'},
    '/?/fixedEstablishments/?/address/buildingNumber': {label: 'Número'},
    '/?/fixedEstablishments/?/address/floorNumber': {label: 'Andar'},
    '/?/fixedEstablishments/?/address/postCode': {
      label: 'Código Postal',
      validations: {
        ADDRESS_POST_CODE_INVALID_FORMAT: 'O código postal tem de ser preenchido com formato NNNN-NNN.'
      }
    },
    '/?/fixedEstablishments/?/address/city': {label: 'Localidade'},
    '/?/fixedEstablishments/?/address/country': {
      label: 'País',
      [I18N_SELECTABLE_OPTIONS_KEY]: fixedEstabAddressCountries
    },
    '/?/fixedEstablishments/?/address/region': {
      label: 'Região/Território',
      [I18N_VALUE_HELP_MESSAGE_KEY]: 'Territórios ou regiões  sujeitos a fiscalidade privilegiada',
      [I18N_SELECTABLE_OPTIONS_KEY]: peculiarAddressCountriesByFieldFn('../country'),
      [I18N_SELECTABLE_NO_OPTIONS_TEXT_KEY]: 'Sem opções',
      validations: {
        ADDRESS_REGION_WRONG_COUNTRY: 'Esta região não pertence ao [país](lf://../country) preenchido.',
        ADDRESS_WITH_ARTIFICIAL_CODE_NOT_ALLOWED: 'A [região](lf://../region) é inválida.',
      },
    },
    '/?/fixedEstablishments/?/address/suite': {label: 'Fracção'},
    '/?/fixedEstablishments/?/address/postOfficeBox': {label: 'Apartado'},
    '/?/fixedEstablishments/?/address/district': {label: 'Distrito'},
    '/?/fixedEstablishments/?/address/countrySubEntity': {label: 'Região Autónoma'},
    '/?/fixedEstablishments/?/address/otherLocalId': {label: 'Outro'},
    '/?/fixedEstablishments/?/address/freeText': {label: 'Morada Livre'},

    // Identification in other EU countries / Member States
    '/?/idsInOtherMSs': {
      [I18N_TABLE_COLUMN_LABELS_KEY]: taxVatIdColumnNames
    },
    '/?/idsInOtherMSs/?/issuingCountry': {
      label: 'País',
      [I18N_SELECTABLE_OPTIONS_KEY]: idsInOtherMSsCountries
    },
    '/?/idsInOtherMSs/?/vatIdNum': {label: 'Número IVA'},
    // Previous registrations for special schemes
    '/?/prevOrCurrOssVatId': {
      label: 'Registos anteriores/atuais',
      headerText: 'Registo em Regimes Especiais noutros Estados-Membros',
      subHeaderText: 'Em vigor a partir de 2021/07/01',
      [I18N_VALUE_CODE_KEY]: '21',
      [I18N_TABLE_COLUMN_LABELS_KEY]: taxVatIdColumnNames
    },
    '/?/prevOrCurrOssVatId/?/scheme': {
      label: 'Regime',
      [I18N_SELECTABLE_OPTIONS_KEY]: prevOrCurrOssVatIdSchemes
    },
    '/?/prevOrCurrOssVatId/?/issuingCountry': {
      label: 'País',
      [I18N_SELECTABLE_OPTIONS_KEY]: prevOrCurrOssVatIdCountries
    },
    '/?/prevOrCurrOssVatId/?/vatIdNum': {label: 'Número IVA'},

    // Supply Commencement
    '/?/supplyCommencement': {
      label: 'Início de fornecimento',
    },
    '/?/supplyCommencement/alreadyCarriedOutSupplies': {
      label: 'Já começou o fornecimento de serviços ao abrigo deste regime?',
      [I18N_SELECTABLE_OPTIONS_KEY]: yesNoOptions
    },
    '/?/supplyCommencement/commencementDate': {
      [I18N_VALUE_CODE_KEY]: '17',
      label: 'Data de início da utilização do regime'
    },

    // Relevant Dates
    '/?/relevantDates': {
      label: 'Datas relevantes'
    },
    '/?/relevantDates/registrationReqDate': {
      label: 'Data do pedido de registo',
      [I18N_VALUE_CODE_KEY]: '18',
      [I18N_VALUE_HELP_MESSAGE_KEY]: 'Data do pedido de registo no regime apresentado pelo sujeito passivo',
    },
    '/?/relevantDates/forecastStartDate': {
      label: 'Data de início',
      [I18N_VALUE_CODE_KEY]: '17',
      [I18N_VALUE_HELP_MESSAGE_KEY]: 'Data de início da utilização do regime'
    },
    '/?/relevantDates/msidDecisionDate': {
      label: 'Data de registo',
      [I18N_VALUE_CODE_KEY]: '19',
      [I18N_VALUE_HELP_MESSAGE_KEY]: 'Data da decisão de registo tomada pelo Estado-Membro de identificação'
    },

    // Consent / Change of MSID
    '/?/consents': {
      label: 'Confirmações',
      changeMSIDNotice: 'Se já usa este regime noutro país da UE e fechou ou vai fechar o seu estabelecimento estável nesse país, e quer continuar a usar o regime registando-se em Portugal, por favor indique o número de identificação de IVA com que está registado no noutro país da UE. Note-se que para que o seu registo seja aceite, precisa de se desregistar no outro país UE antes do fim do décimo dia após o fim deste mês.',
      changeMSIDHeader: 'Mudança de país de identificação',
    },

  }));
